import { useCallback } from 'react';
import api from '../services/api';
import { useApi } from './useApi';
import { useAppDispatch } from '../store/hooks';
import { setCredentials, clearCredentials } from '../store/slices/authSlice';

interface AuthUser {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  lastLogin?: string;
}

interface AuthPayload {
  token: string;
  user: AuthUser;
}

export function useLogin() {
  const dispatch = useAppDispatch();

  const loginFn = useCallback(
    async (email: string, password: string): Promise<AuthPayload> => {
      const { data } = await api.post('/auth/login', { email, password });
      const payload: AuthPayload = data.data;
      localStorage.setItem('flygds_token', payload.token);
      localStorage.setItem('flygds_user', JSON.stringify(payload.user));
      dispatch(setCredentials(payload));
      return payload;
    },
    [dispatch],
  );

  return useApi<AuthPayload>(loginFn);
}

export function useRegister() {
  const dispatch = useAppDispatch();

  const registerFn = useCallback(
    async (name: string, email: string, password: string): Promise<AuthPayload> => {
      const { data } = await api.post('/auth/register', { name, email, password });
      const payload: AuthPayload = data.data;
      localStorage.setItem('flygds_token', payload.token);
      localStorage.setItem('flygds_user', JSON.stringify(payload.user));
      dispatch(setCredentials(payload));
      return payload;
    },
    [dispatch],
  );

  return useApi<AuthPayload>(registerFn);
}

export function useProfile() {
  const fetchProfile = useCallback(async (): Promise<AuthUser> => {
    const { data } = await api.get('/auth/me');
    return data.data.user;
  }, []);

  return useApi<AuthUser>(fetchProfile);
}

export function useUpdateProfile() {
  const dispatch = useAppDispatch();

  const updateFn = useCallback(
    async (name: string): Promise<AuthUser> => {
      const { data } = await api.patch('/auth/me', { name });
      const user: AuthUser = data.data.user;
      localStorage.setItem('flygds_user', JSON.stringify(user));
      dispatch(setCredentials({ token: localStorage.getItem('flygds_token')!, user }));
      return user;
    },
    [dispatch],
  );

  return useApi<AuthUser>(updateFn);
}

export function useChangePassword() {
  const changePwFn = useCallback(
    async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
      const { data } = await api.patch('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return { message: data.message };
    },
    [],
  );

  return useApi<{ message: string }>(changePwFn);
}

export function useLogout() {
  const dispatch = useAppDispatch();

  return useCallback(() => {
    localStorage.removeItem('flygds_token');
    localStorage.removeItem('flygds_user');
    dispatch(clearCredentials());
  }, [dispatch]);
}